import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dropdown,
  DropdownItem,
  getColor,
  Icons,
  InputField,
  Typography,
} from "fds";
import "./index.scss";
import dayjs from "dayjs";
import { FilterData } from "../../types/adminFilter";
import classNames from "classnames";
import { t } from "i18next";
import { CommonProps } from "fds/src/helpers/props";
import { Workgroup } from "../../types/workgroup";
import useCheckboxGroup from "fds/src/hooks/useCheckboxGroup";
import DateDropdown from "../DateDropdown";
import utilsCommon from "../../shared/utils/common";
import { getWorkspaceCode } from "../../shared/utils/auth";
import useWorkspaceStore from "../../redux/dispatcher/useWorkspaceStore";
import useAdminDeptStore from "../../redux/dispatcher/useAdminDeptStore";

interface AdminFilterProps extends CommonProps {
  initFilterData: FilterData;
  filterData: FilterData;
  setFilterData: any;
  search?: any;
  workgroupList?: Workgroup[];
}
const AdminFilter: React.FC<AdminFilterProps> = ({
  initFilterData,
  filterData,
  setFilterData,
  search,
  workgroupList,
  classes,
}) => {
  const { getInternalUserList } = useAdminDeptStore();
  const { getWorkspaceMemberList } = useWorkspaceStore();
  const [buttonClick, setButtonClick] = useState(0);
  const [userId, setUserId] = useState<string>("");
  const [workgroupId, setWorkgroupId] = useState<string>("");
  const [userType, setUserType] = useState<string>("user_all");
  const [accessType, setAccessType] = useState<string>("");
  const [agentType, setAgentType] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [fileManager, setFileManager] = useState<string>("");
  const [workgroupMaster, setWorkgroupMaster] = useState<string>("");
  const [workgroupName, setWorkgroupName] = useState<string>("");
  const [member, setMember] = useState<string>("");
  const workgroupIds = useCheckboxGroup();
  const filterWorkgroups = useCheckboxGroup([0, 1, 2, 3, 4]);
  const filterDocuments = useCheckboxGroup([
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11,
  ]);

  const userTypes = [
    { key: "user_all", label: t("admin.filter.userAll") },
    { key: "user_admin", label: t("admin.filter.userAdmin") },
    { key: "user_internal", label: t("admin.filter.userInternal") },
    { key: "user_external", label: t("admin.filter.userExternal") },
    { key: "user_select", label: t("admin.filter.userSelect") },
  ];

  const accessTypes = [
    { key: "", label: t("admin.filter.accessPlace") },
    { key: "ADMIN", label: t("admin.filter.adminArea") },
    { key: "USER", label: t("admin.filter.userArea") },
    { key: "URL", label: t("admin.filter.urlArea") },
  ];

  const agentTypes = [
    { key: "", label: t("admin.filter.platformAll") },
    { key: "WEB", label: t("admin.filter.webBrowser") },
    { key: "CLIENT", label: t("admin.filter.wrapsodyEcoClient") },
    { key: "MOBILE", label: t("admin.filter.mobileApp") },
  ];

  const filterWorkgroup = [
    { key: 0, label: t("log.filterWorkgroup.0") },
    { key: 1, label: t("log.filterWorkgroup.1") },
    { key: 2, label: t("log.filterWorkgroup.2") },
    { key: 3, label: t("log.filterWorkgroup.3") },
    { key: 4, label: t("log.filterWorkgroup.4") },
  ];

  const filterDocument = [
    { key: 0, label: t("log.filterDocument.0") },
    { key: 1, label: t("log.filterDocument.1") },
    { key: 2, label: t("log.filterDocument.2") },
    { key: 3, label: t("log.filterDocument.3") },
    { key: 4, label: t("log.filterDocument.4") },
    { key: 5, label: t("log.filterDocument.5") },
    { key: 6, label: t("log.filterDocument.6") },
    { key: 7, label: t("log.filterDocument.7") },
    { key: 8, label: t("log.filterDocument.8") },
    { key: 9, label: t("log.filterDocument.9") },
    { key: 10, label: t("log.filterDocument.10") },
    { key: 11, label: t("log.filterDocument.11") },
  ];

  return (
    <Box classes={classNames("adminFilter", classes)}>
      <Button
        onClick={() => {
          setFilterData(initFilterData);
        }}
        btnStyle="link"
        type="secondary"
        size="sm"
        leadingIcon={
          <Icons
            variant="line"
            label="redoLeft"
            size={16}
            stroke={
              JSON.stringify(initFilterData) !== JSON.stringify(filterData)
                ? getColor("COLOR_TEXT_PRIMARY")
                : getColor("COLOR_TEXT_DISABLED")
            }
          />
        }
      />

      {filterData.workgroupName !== undefined && (
        <Dropdown
          btnStyle="round"
          btnSize="rd"
          buttonWidth={320}
          placeholder={t("admin.filter.workgroup")}
          value={
            filterData.workgroupName === ""
              ? undefined
              : filterData.workgroupName + ""
          }
          showValue={true}
          buttonClick={buttonClick}
          classes="mg ml-12"
        >
          <Box classes="adminFilter-user" direction="column">
            <Box classes="adminFilter-user-title">
              {t("admin.filter.workgroup")}
            </Box>
            <InputField
              classes="adminFilter-user-search"
              leadingIcon={<Icons label="search" variant="line" />}
              placeholder={t("admin.filter.inputSearch")}
              value={workgroupName}
              onChange={(e) => {
                setWorkgroupName(e.target.value);
              }}
            />
            <Box classes="adminFilter-user-footer">
              <Button
                btnStyle="fill"
                label={t("admin.common.button.cancel")}
                type="secondary"
                onClick={() => {
                  setWorkgroupName("");
                  setButtonClick(buttonClick + 1);
                }}
              />
              <Button
                btnStyle="fill"
                label={t("admin.common.button.apply")}
                onClick={() => {
                  setFilterData({
                    ...filterData,
                    workgroupName: workgroupName,
                  });
                  setButtonClick(buttonClick + 1);
                }}
              />
            </Box>
          </Box>
        </Dropdown>
      )}

      {filterData.workgroupMaster !== undefined && (
        <Dropdown
          btnStyle="round"
          btnSize="rd"
          buttonWidth={320}
          placeholder={t("admin.filter.workgroupManager")}
          value={
            filterData.workgroupMaster === ""
              ? undefined
              : filterData.workgroupMaster + ""
          }
          showValue={true}
          buttonClick={buttonClick}
          classes="mg ml-12"
        >
          <Box classes="adminFilter-user" direction="column">
            <Box classes="adminFilter-user-title">
              {t("admin.filter.workgroupManager")}
            </Box>
            <InputField
              classes="adminFilter-user-search"
              leadingIcon={<Icons label="search" variant="line" />}
              placeholder={t("admin.filter.inputSearch")}
              value={workgroupMaster}
              onChange={(e) => {
                setWorkgroupMaster(e.target.value);
              }}
            />
            <Box classes="adminFilter-user-footer">
              <Button
                btnStyle="fill"
                label={t("admin.common.button.cancel")}
                type="secondary"
                onClick={() => {
                  setWorkgroupMaster("");
                  setButtonClick(buttonClick + 1);
                }}
              />
              <Button
                btnStyle="fill"
                label={t("admin.common.button.apply")}
                onClick={async () => {
                  let resp: any = await (window.eco.serviceType !== "OCI"
                    ? getInternalUserList(workgroupMaster)
                    : getWorkspaceMemberList({
                        searchOpt: workgroupMaster,
                        userType: null,
                        isAdminPage: true,
                        order: "user",
                        dsdCode: getWorkspaceCode() ?? "",
                        tenancyCode: getWorkspaceCode() ?? "",
                      }));
                  if (resp.payload) {
                    setFilterData({
                      ...filterData,
                      workgroupMaster:
                        window.eco.serviceType !== "OCI"
                          ? resp.payload?.users[0]?.id ?? ""
                          : resp.payload[0]?.userId ?? "",
                    });
                    setButtonClick(buttonClick + 1);
                  }
                }}
              />
            </Box>
          </Box>
        </Dropdown>
      )}

      {filterData.member !== undefined && (
        <Dropdown
          btnStyle="round"
          btnSize="rd"
          buttonWidth={320}
          placeholder={t("admin.filter.member")}
          value={filterData.member === "" ? undefined : filterData.member + ""}
          showValue={true}
          buttonClick={buttonClick}
          classes="mg ml-12"
        >
          <Box classes="adminFilter-user" direction="column">
            <Box classes="adminFilter-user-title">
              {t("admin.filter.member")}
            </Box>
            <InputField
              classes="adminFilter-user-search"
              leadingIcon={<Icons label="search" variant="line" />}
              placeholder={t("admin.filter.inputSearch")}
              value={member}
              onChange={(e) => {
                setMember(e.target.value);
              }}
            />
            <Box classes="adminFilter-user-footer">
              <Button
                btnStyle="fill"
                label={t("admin.common.button.cancel")}
                type="secondary"
                onClick={() => {
                  setMember("");
                  setButtonClick(buttonClick + 1);
                }}
              />
              <Button
                btnStyle="fill"
                label={t("admin.common.button.apply")}
                onClick={async () => {
                  let resp: any = await (window.eco.serviceType !== "OCI"
                    ? getInternalUserList(member)
                    : getWorkspaceMemberList({
                        searchOpt: member,
                        userType: null,
                        isAdminPage: true,
                        order: "user",
                        dsdCode: getWorkspaceCode() ?? "",
                        tenancyCode: getWorkspaceCode() ?? "",
                      }));
                  if (resp.payload) {
                    setFilterData({
                      ...filterData,
                      member:
                        window.eco.serviceType !== "OCI"
                          ? resp.payload?.users[0]?.id ?? ""
                          : resp.payload[0]?.userId ?? "",
                    });
                    setButtonClick(buttonClick + 1);
                  }
                }}
              />
            </Box>
          </Box>
        </Dropdown>
      )}

      {filterData.userId !== undefined && filterData.userType === undefined && (
        <Dropdown
          btnStyle="round"
          btnSize="rd"
          buttonWidth={320}
          placeholder={t("admin.filter.user")}
          value={filterData.userId === "" ? undefined : filterData.userId + ""}
          showValue={true}
          buttonClick={buttonClick}
          classes="mg ml-12"
        >
          <Box classes="adminFilter-user" direction="column">
            <Box classes="adminFilter-user-title">{t("admin.filter.user")}</Box>
            <InputField
              classes="adminFilter-user-search"
              leadingIcon={<Icons label="search" variant="line" />}
              placeholder={t("admin.filter.inputUserId")}
              value={userId}
              onChange={(e) => {
                setUserId(e.target.value);
              }}
            />
            <Box classes="adminFilter-user-footer">
              <Button
                btnStyle="fill"
                label={t("admin.common.button.cancel")}
                type="secondary"
                onClick={() => {
                  setUserId("");
                  setButtonClick(buttonClick + 1);
                }}
              />
              <Button
                btnStyle="fill"
                label={t("admin.common.button.apply")}
                onClick={async () => {
                  let resp: any = await (window.eco.serviceType !== "OCI"
                    ? getInternalUserList(userId)
                    : getWorkspaceMemberList({
                        searchOpt: userId,
                        userType: null,
                        isAdminPage: true,
                        order: "user",
                        dsdCode: getWorkspaceCode() ?? "",
                        tenancyCode: getWorkspaceCode() ?? "",
                      }));
                  if (resp.payload) {
                    setFilterData({
                      ...filterData,
                      userId:
                        window.eco.serviceType !== "OCI"
                          ? resp.payload?.users[0]?.id ?? ""
                          : resp.payload[0]?.userId ?? "",
                    });
                    setButtonClick(buttonClick + 1);
                  }
                }}
              />
            </Box>
          </Box>
        </Dropdown>
      )}

      {filterData.userId !== undefined && filterData.userType !== undefined && (
        <Dropdown
          btnStyle="round"
          btnSize="rd"
          buttonWidth={320}
          placeholder={t("admin.filter.user")}
          value={
            filterData.userType === "user_all"
              ? undefined
              : userTypes.find((item) => item.key === filterData.userType)
                  ?.label
          }
          showValue={true}
          buttonClick={buttonClick}
          classes="mg ml-12"
        >
          <Box classes="adminFilter-user" direction="column">
            {userTypes.map((item) => (
              <DropdownItem
                type="check"
                label={item.label}
                checked={userType === item.key}
                checkColor={userType === item.key}
                onClick={() => {
                  setUserType(item.key);
                }}
              />
            ))}
            {userType === "user_select" && (
              <>
                <DropdownItem type="default" state="divider" />
                <InputField
                  classes="adminFilter-user-search"
                  leadingIcon={<Icons label="search" variant="line" />}
                  placeholder={t("admin.filter.inputUserId")}
                  value={userId}
                  onChange={(e) => {
                    setUserId(e.target.value);
                  }}
                />
              </>
            )}
            <Box classes="adminFilter-user-footer">
              <Button
                btnStyle="fill"
                label={t("admin.common.button.cancel")}
                type="secondary"
                onClick={() => {
                  setUserId(filterData.userId + "");
                  setUserType(filterData.userType + "");
                  setButtonClick(buttonClick + 1);
                }}
              />
              <Button
                btnStyle="fill"
                label={t("admin.common.button.apply")}
                onClick={() => {
                  setFilterData({
                    ...filterData,
                    userId: userType === "user_select" ? userId : "",
                    userType: userType,
                  });
                  if (userType !== "user_select") {
                    setUserId("");
                  }
                  setButtonClick(buttonClick + 1);
                }}
              />
            </Box>
          </Box>
        </Dropdown>
      )}

      {filterData.accessType !== undefined && (
        <Dropdown
          btnStyle="round"
          btnSize="rd"
          buttonWidth={240}
          placeholder={t("admin.log.accessPlace")}
          value={
            filterData.accessType === ""
              ? undefined
              : accessTypes.find((item) => item.key === filterData.accessType)
                  ?.label
          }
          showValue={true}
          buttonClick={buttonClick}
          classes="mg ml-12"
        >
          <Box classes="adminFilter-user" direction="column">
            {accessTypes.map((item) => (
              <DropdownItem
                type="check"
                label={item.label}
                checked={accessType === item.key}
                checkColor={accessType === item.key}
                onClick={() => {
                  setAccessType(item.key);
                }}
              />
            ))}
            <Box classes="adminFilter-user-footer">
              <Button
                btnStyle="fill"
                label={t("admin.common.button.cancel")}
                type="secondary"
                onClick={() => {
                  setAccessType(filterData.accessType + "");
                  setButtonClick(buttonClick + 1);
                }}
              />
              <Button
                btnStyle="fill"
                label={t("admin.common.button.apply")}
                onClick={() => {
                  setFilterData({
                    ...filterData,
                    accessType: accessType,
                  });
                  setButtonClick(buttonClick + 1);
                }}
              />
            </Box>
          </Box>
        </Dropdown>
      )}

      {filterData.agentType !== undefined && (
        <Dropdown
          btnStyle="round"
          btnSize="rd"
          buttonWidth={240}
          placeholder={t("admin.log.platform")}
          value={
            filterData.agentType === ""
              ? undefined
              : agentTypes.find((item) => item.key === filterData.agentType)
                  ?.label
          }
          showValue={true}
          buttonClick={buttonClick}
          classes="mg ml-12"
        >
          <Box classes="adminFilter-user" direction="column">
            {agentTypes.map((item) => (
              <DropdownItem
                type="check"
                label={item.label}
                checked={agentType === item.key}
                checkColor={agentType === item.key}
                onClick={() => {
                  setAgentType(item.key);
                }}
              />
            ))}
            <Box classes="adminFilter-user-footer">
              <Button
                btnStyle="fill"
                label={t("admin.common.button.cancel")}
                type="secondary"
                onClick={() => {
                  setAgentType(filterData.agentType + "");
                  setButtonClick(buttonClick + 1);
                }}
              />
              <Button
                btnStyle="fill"
                label={t("admin.common.button.apply")}
                onClick={() => {
                  setFilterData({
                    ...filterData,
                    agentType: agentType,
                  });
                  setButtonClick(buttonClick + 1);
                }}
              />
            </Box>
          </Box>
        </Dropdown>
      )}

      {filterData.fileName !== undefined && (
        <Dropdown
          btnStyle="round"
          btnSize="rd"
          buttonWidth={320}
          placeholder={t("admin.filter.fileName")}
          value={
            filterData.fileName === "" ? undefined : filterData.fileName + ""
          }
          showValue={true}
          buttonClick={buttonClick}
          classes="mg ml-12"
        >
          <Box classes="adminFilter-user" direction="column">
            <Box classes="adminFilter-user-title">
              {t("admin.filter.fileName")}
            </Box>
            <InputField
              classes="adminFilter-user-search"
              leadingIcon={<Icons label="search" variant="line" />}
              placeholder={t("admin.filter.inputFileName")}
              value={fileName}
              onChange={(e) => {
                setFileName(e.target.value);
              }}
            />
            <Box classes="adminFilter-user-footer">
              <Button
                btnStyle="fill"
                label={t("admin.common.button.cancel")}
                type="secondary"
                onClick={() => {
                  setFileName("");
                  setButtonClick(buttonClick + 1);
                }}
              />
              <Button
                btnStyle="fill"
                label={t("admin.common.button.apply")}
                onClick={() => {
                  setFilterData({
                    ...filterData,
                    fileName: fileName,
                  });
                  setButtonClick(buttonClick + 1);
                }}
              />
            </Box>
          </Box>
        </Dropdown>
      )}

      {filterData.fileManager !== undefined && (
        <Dropdown
          btnStyle="round"
          btnSize="rd"
          buttonWidth={320}
          placeholder={t("admin.filter.fileManager")}
          value={
            filterData.fileManager === ""
              ? undefined
              : filterData.fileManager + ""
          }
          showValue={true}
          buttonClick={buttonClick}
          classes="mg ml-12"
        >
          <Box classes="adminFilter-user" direction="column">
            <Box classes="adminFilter-user-title">
              {t("admin.filter.fileManager")}
            </Box>
            <InputField
              classes="adminFilter-user-search"
              leadingIcon={<Icons label="search" variant="line" />}
              placeholder={t("admin.filter.inputSearch")}
              value={fileManager}
              onChange={(e) => {
                setFileManager(e.target.value);
              }}
            />
            <Box classes="adminFilter-user-footer">
              <Button
                btnStyle="fill"
                label={t("admin.common.button.cancel")}
                type="secondary"
                onClick={() => {
                  setFileManager("");
                  setButtonClick(buttonClick + 1);
                }}
              />
              <Button
                btnStyle="fill"
                label={t("admin.common.button.apply")}
                onClick={() => {
                  setFilterData({
                    ...filterData,
                    fileManager: fileManager,
                  });
                  setButtonClick(buttonClick + 1);
                }}
              />
            </Box>
          </Box>
        </Dropdown>
      )}

      {filterData.saveTypes !== undefined && (
        <Dropdown
          btnStyle="round"
          placeholder={t("admin.filter.type")}
          buttonWidth={290}
          value={
            filterData.saveTypes.length === 0 ||
            filterData.saveTypes.length === filterDocument.length
              ? undefined
              : filterData.saveTypes.length + ""
          }
          showValue={true}
          buttonClick={buttonClick}
          classes="mg ml-12"
        >
          <Box width={274} maxHeight={388} classes={"fav-checkbox"}>
            <Box
              height={26}
              width="100%"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                type="caption1_rg"
                classes="mg ml-8"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("admin.filter.type")}
              </Typography>
              <Button
                btnStyle="link"
                label={
                  filterDocuments.selected.length === filterDocument.length
                    ? t("admin.filter.unselectAll")
                    : t("admin.filter.selectAll")
                }
                onClick={() =>
                  filterDocuments.selected.length === filterDocument.length
                    ? filterDocuments.handleAllClick([])
                    : filterDocuments.handleAllClick(
                        filterDocument.map((item) => item.key)
                      )
                }
                textColor={getColor("COLOR_TEXT_BRAND")}
              />
            </Box>
            <Box classes="mg mt-4 fav-workgroups">
              {filterDocument.map((item) => (
                <Checkbox
                  label={item.label}
                  onClick={() => filterDocuments.handleClick(item.key)}
                  check={filterDocuments.isSelected(item.key)}
                />
              ))}
            </Box>
            <Box classes="fav-checkbox--bottom">
              <Button
                label={t("admin.common.button.cancel")}
                type="secondary"
                btnStyle="fill"
                onClick={() => {
                  filterData.saveTypes &&
                    filterDocuments.handleAllClick(filterData.saveTypes);
                  setButtonClick(buttonClick + 1);
                }}
              />
              <Button
                label={t("admin.common.button.apply")}
                classes="mg ml-10"
                btnStyle="fill"
                onClick={() => {
                  setFilterData({
                    ...filterData,
                    saveTypes: filterDocuments.selected,
                  });
                  setButtonClick(buttonClick + 1);
                }}
              />
            </Box>
          </Box>
        </Dropdown>
      )}

      {filterData.filterWorkgroup !== undefined &&
        filterData.filterDocument !== undefined && (
          <Dropdown
            btnStyle="round"
            placeholder={t("admin.filter.type")}
            buttonWidth={290}
            value={
              filterData.filterWorkgroup.length +
                filterData.filterDocument.length ===
                0 ||
              filterData.filterWorkgroup.length +
                filterData.filterDocument.length ===
                filterWorkgroup.length
                ? undefined
                : filterData.filterWorkgroup.length +
                  filterData.filterDocument.length +
                  ""
            }
            showValue={true}
            buttonClick={buttonClick}
            classes="mg ml-12"
          >
            <Box width={274} maxHeight={388} classes={"fav-checkbox"}>
              <Box
                height={26}
                width="100%"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  type="caption1_rg"
                  classes="mg ml-8"
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                >
                  {t("admin.filter.type")}
                </Typography>
                <Button
                  btnStyle="link"
                  label={
                    filterWorkgroups.selected.length === filterWorkgroup.length
                      ? t("admin.filter.unselectAll")
                      : t("admin.filter.selectAll")
                  }
                  onClick={() =>
                    filterWorkgroups.selected.length === filterWorkgroup.length
                      ? filterWorkgroups.handleAllClick([])
                      : filterWorkgroups.handleAllClick(
                          filterWorkgroup.map((item) => item.key)
                        )
                  }
                  textColor={getColor("COLOR_TEXT_BRAND")}
                />
              </Box>
              <Box classes="mg mt-4 fav-workgroups">
                {filterWorkgroup.map((item) => (
                  <Checkbox
                    label={item.label}
                    onClick={() => filterWorkgroups.handleClick(item.key)}
                    check={filterWorkgroups.isSelected(item.key)}
                  />
                ))}
              </Box>
              <Box classes="fav-checkbox--bottom">
                <Button
                  label={t("admin.common.button.cancel")}
                  type="secondary"
                  btnStyle="fill"
                  onClick={() => {
                    if (
                      filterData.filterWorkgroup &&
                      filterData.filterDocument
                    ) {
                      let tempIds = [] as number[];

                      filterData.filterWorkgroup.map((item) =>
                        tempIds.push(item)
                      );
                      filterData.filterDocument.map((item) =>
                        tempIds.push(item + 100)
                      );
                      filterWorkgroups.handleAllClick(tempIds);
                    }
                    setButtonClick(buttonClick + 1);
                  }}
                />
                <Button
                  label={t("admin.common.button.apply")}
                  classes="mg ml-10"
                  btnStyle="fill"
                  onClick={() => {
                    setFilterData({
                      ...filterData,
                      filterWorkgroup: filterWorkgroups.selected
                        .filter((item: number) => item < 100)
                        .map((item: number) => item),
                      filterDocument: filterWorkgroups.selected
                        .filter((item: number) => item > 100)
                        .map((item: number) => Number(item - 100)),
                    });
                    setButtonClick(buttonClick + 1);
                  }}
                />
              </Box>
            </Box>
          </Dropdown>
        )}

      {filterData.workgroupId !== undefined && workgroupList && (
        <Dropdown
          btnStyle="round"
          placeholder={t("admin.filter.workgroup")}
          buttonWidth={290}
          value={
            filterData.workgroupId === ""
              ? undefined
              : filterData.workgroupId === "NONEWORKGROUP"
              ? "My Box"
              : workgroupList.filter(
                  (item: Workgroup) =>
                    item.workgroupId === filterData.workgroupId
                ).length > 0
              ? workgroupList.filter(
                  (item: Workgroup) =>
                    item.workgroupId === filterData.workgroupId
                )[0].workgroupName
              : undefined
          }
          showValue={true}
          buttonClick={buttonClick}
          classes="mg ml-12"
        >
          <Box width={274} maxHeight={388} classes={"fav-checkbox"}>
            <Box classes="mg mt-4 fav-workgroups">
              <DropdownItem
                type="check"
                label={t("admin.filter.workgroupAll")}
                checked={workgroupId === ""}
                checkColor={workgroupId === ""}
                onClick={() => {
                  setWorkgroupId("");
                }}
              />
              <DropdownItem
                type="check"
                label={"My Box"}
                checked={workgroupId === "NONEWORKGROUP"}
                checkColor={workgroupId === "NONEWORKGROUP"}
                onClick={() => {
                  setWorkgroupId("NONEWORKGROUP");
                }}
              />
              {workgroupList.map((workgroup) => (
                <DropdownItem
                  type="check"
                  label={workgroup.workgroupName}
                  checked={workgroupId === workgroup.workgroupId}
                  checkColor={workgroupId === workgroup.workgroupId}
                  onClick={() => {
                    setWorkgroupId(workgroup.workgroupId);
                  }}
                />
              ))}
            </Box>
            <Box classes="fav-checkbox--bottom">
              <Button
                label={t("admin.common.button.cancel")}
                type="secondary"
                btnStyle="fill"
                onClick={() => {
                  setWorkgroupId(filterData.workgroupId + "");
                  setButtonClick(buttonClick + 1);
                }}
              />
              <Button
                label={t("admin.common.button.apply")}
                classes="mg ml-10"
                btnStyle="fill"
                onClick={() => {
                  setFilterData({
                    ...filterData,
                    workgroupId: workgroupId,
                  });
                  setButtonClick(buttonClick + 1);
                }}
              />
            </Box>
          </Box>
        </Dropdown>
      )}

      {filterData.workgroupIds !== undefined && workgroupList && (
        <Dropdown
          btnStyle="round"
          placeholder={t("admin.filter.workgroup")}
          buttonWidth={290}
          value={
            filterData.workgroupIds.length === 0
              ? undefined
              : filterData.workgroupIds.length + ""
          }
          showValue={true}
          buttonClick={buttonClick}
          classes="mg ml-12"
        >
          <Box width={274} maxHeight={388} classes={"fav-checkbox"}>
            <Box
              height={26}
              width="100%"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                type="caption1_rg"
                classes="mg ml-8"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("admin.filter.name")}
              </Typography>
              <Button
                btnStyle="link"
                label={
                  workgroupIds.selected.length === workgroupList.length
                    ? t("admin.filter.unselectAll")
                    : t("admin.filter.selectAll")
                }
                onClick={() =>
                  workgroupIds.selected.length === workgroupList.length
                    ? workgroupIds.handleAllClick([])
                    : workgroupIds.handleAllClick(
                        workgroupList.map((item) => item.workgroupId)
                      )
                }
                textColor={getColor("COLOR_TEXT_BRAND")}
              />
            </Box>
            <Box classes="mg mt-4 fav-workgroups">
              {workgroupList.map((workgroup) => (
                <Checkbox
                  label={workgroup.workgroupName}
                  onClick={() =>
                    workgroupIds.handleClick(workgroup.workgroupId)
                  }
                  check={workgroupIds.isSelected(workgroup.workgroupId)}
                />
              ))}
            </Box>
            <Box classes="fav-checkbox--bottom">
              <Button
                label={t("admin.common.button.cancel")}
                type="secondary"
                btnStyle="fill"
                onClick={() => {
                  filterData.workgroupIds &&
                    workgroupIds.handleAllClick(filterData.workgroupIds);
                  setButtonClick(buttonClick + 1);
                }}
              />
              <Button
                label={t("admin.common.button.apply")}
                classes="mg ml-10"
                btnStyle="fill"
                onClick={() => {
                  setFilterData({
                    ...filterData,
                    workgroupIds: workgroupIds.selected,
                  });
                  setButtonClick(buttonClick + 1);
                }}
              />
            </Box>
          </Box>
        </Dropdown>
      )}

      {filterData.dateFilter !== undefined && (
        <DateDropdown
          filter={filterData}
          buttonClick={buttonClick}
          dateFilter={filterData.dateFilter}
          setButtonClick={setButtonClick}
          changeFilterByKey={(key: string, value: any) => {
            if (typeof value !== "object") {
              setFilterData({
                ...filterData,
                dateFilter: value,
                date: {
                  start: utilsCommon.getStartPeriod(value),
                  end: dayjs().endOf("day").valueOf(),
                },
              });
            } else {
              setFilterData({
                ...filterData,
                dateFilter: "custom",
                date: {
                  start: dayjs(value.start).valueOf(),
                  end: dayjs(value.end).valueOf(),
                },
              });
            }
          }}
        />
      )}
    </Box>
  );
};
export default AdminFilter;
